import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import { formatDateOnly } from '../../lib/dates';
import {
  activeServiceRequestFilterCount,
  fetchImplementations,
  fetchServiceRequestDashboard,
  fetchServiceRequestList,
  matchesServiceRequestScope,
  parseServiceRequestListFilters,
  personName,
  serviceRequestListFiltersToSearchParams,
  serviceRequestFrom,
  serviceRequestScopePatch,
  EMPTY_SERVICE_REQUEST_FILTERS,
  SERVICE_REQUEST_SCOPES,
  SR_PANEL,
  type ImplementationSlim,
  type PersonRef,
  type ServiceRequestDashboard,
  type ServiceRequestListFilters,
  type ServiceRequestListItem,
} from '../../lib/service-request-views';
import { fetchServiceRequestWorkspace, listServiceRequestTypes, serviceRequestPath, type ServiceRequestType } from '../../lib/service-requests';
import { cn } from '../../lib/utils';
import { memberDisplayName, type MemberDto } from '../../lib/work-types';
import { BrandEmpty, ListEmpty, LoadingRows, WorkPageError } from './page-states';
import { ReadinessBar, ServiceRequestPriorityChip, StageChip } from './ServiceRequestBadges';
import ServiceRequestDeleteMenu from './ServiceRequestDeleteMenu';
import ServiceRequestFilters from './ServiceRequestFilters';
import WorkListPagination from './WorkListPagination';

/** Drafts show how much of the form is filled in; submitted requests show delivery readiness. */
function listReadinessPercent(item: ServiceRequestListItem): number {
  if (item.stage === 'draft' || item.stage === 'changes_required') {
    return item.submission_percent ?? (item.submission_complete ? 100 : 0);
  }
  if (item.stage === 'completed') return 100;
  return item.readiness_percent;
}

type Props = {
  /** Reports the filtered total so the page title can show a count. */
  onTotalChange?: (total: number | null) => void;
};

/** Owners are the people who can be assigned on a Service Request. */
async function loadOwners(): Promise<PersonRef[]> {
  const { project } = await fetchServiceRequestWorkspace();
  const members = await apiFetch<{ items: MemberDto[] }>(v1Path(`/projects/${project.id}/members`));
  return members.items
    .filter((member) => member.is_active)
    .map((member) => ({ user_id: member.user_id, name: memberDisplayName(member), email: member.email ?? null }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function TargetDateCell({ value, overdue }: { value: string | null; overdue: boolean }) {
  if (!value) return <span className="text-muted-foreground/50">—</span>;
  const label = formatDateOnly(value) ?? value;
  return (
    <span
      className={cn(
        'whitespace-nowrap font-mono text-xs tabular-nums',
        overdue
          ? 'inline-flex rounded-md bg-red-50 px-1.5 py-0.5 text-red-700 dark:bg-red-950 dark:text-red-300'
          : 'text-muted-foreground',
      )}
    >
      {label}
    </span>
  );
}

export default function ServiceRequestList({ onTotalChange }: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseServiceRequestListFilters(searchParams), [searchParams]);
  const onTotalChangeRef = useRef(onTotalChange);
  onTotalChangeRef.current = onTotalChange;

  const [items, setItems] = useState<ServiceRequestListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [types, setTypes] = useState<ServiceRequestType[]>([]);
  const [owners, setOwners] = useState<PersonRef[]>([]);
  const [implementations, setImplementations] = useState<ImplementationSlim[]>([]);
  const [dashboard, setDashboard] = useState<ServiceRequestDashboard | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listServiceRequestTypes().then((rows) => { if (!cancelled) setTypes(rows); }).catch(() => undefined);
    void loadOwners().then((rows) => { if (!cancelled) setOwners(rows); }).catch(() => undefined);
    void fetchImplementations().then((rows) => { if (!cancelled) setImplementations(rows); }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const loadScopes = useCallback(() => {
    void fetchServiceRequestDashboard().then(setDashboard).catch(() => setDashboard(null));
  }, []);

  useEffect(() => {
    loadScopes();
  }, [loadScopes]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const [list, unfiltered] = await Promise.all([
        fetchServiceRequestList(filters),
        activeServiceRequestFilterCount(filters) > 0
          ? fetchServiceRequestList({ ...EMPTY_SERVICE_REQUEST_FILTERS, limit: 12 })
          : null,
      ]);
      setItems(list.items);
      setTotal(list.page.total);
      setQueueEmpty((unfiltered ?? list).page.total === 0);
      onTotalChangeRef.current?.(list.page.total);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load Service Requests'));
      setItems([]);
      setTotal(0);
      setQueueEmpty(false);
      onTotalChangeRef.current?.(null);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onChanged() {
      void load({ silent: true });
      loadScopes();
    }
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load, loadScopes]);

  function applyFilters(patch: Partial<ServiceRequestListFilters>) {
    const next = { ...filters, ...patch };
    setSearchParams(serviceRequestListFiltersToSearchParams(next, searchParams), { replace: true });
  }

  function clearFilters() {
    setSearchParams(serviceRequestListFiltersToSearchParams(EMPTY_SERVICE_REQUEST_FILTERS, searchParams), { replace: true });
  }

  const backState = serviceRequestFrom(`/service-requests?${searchParams.toString()}`, 'Service Requests');
  const page = Math.floor(filters.offset / filters.limit) + 1;
  const pages = Math.max(1, Math.ceil(total / filters.limit));

  function openRow(item: ServiceRequestListItem, event: MouseEvent | KeyboardEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, input, [role="menuitem"]')) return;
    navigate(serviceRequestPath(item), { state: backState });
  }

  return (
    <>
      <div className={cn(SR_PANEL, 'mb-3 grid grid-cols-2 gap-px overflow-hidden bg-border p-0 sm:grid-cols-3 lg:grid-cols-5')} aria-label="Quick scopes">
        {SERVICE_REQUEST_SCOPES.map((scope) => {
          const active = matchesServiceRequestScope(filters, scope);
          const count = dashboard ? scope.count(dashboard) : null;
          return (
            <button
              key={scope.key}
              type="button"
              aria-pressed={active}
              onClick={() => applyFilters(serviceRequestScopePatch(scope, active))}
              className={cn(
                'flex flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors',
                active ? 'bg-primary/10' : 'bg-background hover:bg-muted/60',
              )}
            >
              <span className="text-xs text-muted-foreground">{scope.label}</span>
              <span
                className={cn(
                  'text-xl font-semibold tabular-nums',
                  count === null && 'text-muted-foreground/40',
                  count && scope.tone === 'danger' && 'text-red-600 dark:text-red-400',
                  count && scope.tone === 'warn' && 'text-amber-600 dark:text-amber-400',
                )}
              >
                {count ?? '—'}
              </span>
            </button>
          );
        })}
      </div>

      <ServiceRequestFilters
        filters={filters}
        options={{ types, owners, implementations }}
        onChange={applyFilters}
        onClear={clearFilters}
      />

      <WorkPageError error={error} />

      {error ? null : loading ? (
        <LoadingRows columns={10} />
      ) : queueEmpty ? (
        <BrandEmpty title="No service requests." />
      ) : items.length === 0 ? (
        <ListEmpty
          title="No matches"
          description="Nothing matches the current filters."
          action={
            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <>
          <div className={cn(SR_PANEL, 'overflow-x-auto')}>
            <Table className="min-w-[64rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[6.5rem]">SR</TableHead>
                  <TableHead className="min-w-[14rem]">Title</TableHead>
                  <TableHead className="w-[9rem]">Customer</TableHead>
                  <TableHead className="w-[10rem]">Type</TableHead>
                  <TableHead className="w-[8.5rem]">Owner</TableHead>
                  <TableHead className="w-[5.5rem]">Priority</TableHead>
                  <TableHead className="w-[10rem]">Status</TableHead>
                  <TableHead className="w-[7.5rem]">Target date</TableHead>
                  <TableHead className="w-[10rem]">Readiness</TableHead>
                  <TableHead className="w-10" aria-label="Actions" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    id={`issue-${item.id}`}
                    tabIndex={0}
                    className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(event) => openRow(item, event)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') openRow(item, event);
                    }}
                    aria-label={`${item.key} ${item.title}`}
                  >
                    <TableCell>
                      <Link
                        to={serviceRequestPath(item)}
                        state={backState}
                        className="whitespace-nowrap font-mono text-[11px] font-semibold tabular-nums text-primary hover:text-primary/80 hover:no-underline"
                      >
                        {item.key}
                      </Link>
                    </TableCell>
                    <TableCell className="min-w-0">
                      <div className="truncate text-sm font-medium" title={item.title}>{item.title}</div>
                      {item.implementation_name ? (
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title={item.implementation_name}>
                          {item.implementation_name}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="truncate text-xs text-muted-foreground" title={item.customer ?? undefined}>
                      {item.customer || <span className="text-muted-foreground/50">—</span>}
                    </TableCell>
                    <TableCell className="truncate text-xs text-muted-foreground" title={item.request_type_name ?? undefined}>
                      {item.request_type_name || <span className="text-muted-foreground/50">—</span>}
                    </TableCell>
                    <TableCell className="truncate text-xs text-muted-foreground">
                      {item.owner ? personName(item.owner) : <span className="text-muted-foreground/50">—</span>}
                    </TableCell>
                    <TableCell>
                      <ServiceRequestPriorityChip priority={item.priority} />
                    </TableCell>
                    <TableCell>
                      <StageChip stage={item.stage} name={item.state_name} />
                    </TableCell>
                    <TableCell>
                      <TargetDateCell value={item.target_go_live_date} overdue={item.sla?.target_overdue ?? false} />
                    </TableCell>
                    <TableCell>
                      <ReadinessBar percent={listReadinessPercent(item)} />
                    </TableCell>
                    <TableCell className="px-1 text-right">
                      <ServiceRequestDeleteMenu request={item} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <WorkListPagination
            page={page}
            pages={pages}
            pageSize={filters.limit}
            shown={items.length}
            total={total}
            onPageChange={(next) => applyFilters({ offset: (next - 1) * filters.limit })}
            onPageSizeChange={(size) => applyFilters({ limit: size, offset: 0 })}
          />
        </>
      )}
    </>
  );
}
